import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import type { DismissByReasonsInput, DismissCandidatesInput, DuplicateCandidateItem, DuplicateCompare, DuplicateReasonGroup, DuplicateSummary, MassMergePreview, MassMergeRun, MergeRecordsInput, Paginated, StartMassMergeInput, UnmergeResult } from '@crm/shared';
import { api } from '../../api/client';

export function useDuplicates(params: { objectType: 'CONTACT' | 'ORGANIZATION'; status: string; page: number; pageSize: number; reasons?: string }, polling = false) {
  return useQuery({
    queryKey: ['duplicates', params],
    queryFn: async () => (await api.get<Paginated<DuplicateCandidateItem>>('/duplicates', { params })).data,
    placeholderData: keepPreviousData,
    refetchInterval: polling ? 2_000 : false,
  });
}
export const useDuplicateSummary = (polling = false) =>
  useQuery({ queryKey: ['duplicates-summary'], queryFn: async () => (await api.get<DuplicateSummary>('/duplicates/summary')).data, refetchInterval: polling ? 2_000 : false });
export function useDuplicateCompare(id: string | undefined) {
  return useQuery({ queryKey: ['duplicate', id], queryFn: async () => (await api.get<DuplicateCompare>(`/duplicates/${id}`)).data, enabled: !!id });
}

const KEYS = [['duplicates'], ['duplicates-summary'], ['duplicate-reasons'], ['contacts'], ['contact'], ['organizations'], ['organization'], ['activities'], ['dashboard']];
function useInvalidate() {
  const qc = useQueryClient();
  return () => KEYS.forEach((k) => void qc.invalidateQueries({ queryKey: k }));
}
export function useScanDuplicates() {
  const inv = useInvalidate();
  return useMutation({ mutationFn: async () => (await api.post('/duplicates/scan')).data, onSuccess: inv });
}
export function useStopScan() {
  const inv = useInvalidate();
  return useMutation({ mutationFn: async () => (await api.post<{ stopped: boolean }>('/duplicates/scan/stop')).data, onSuccess: inv });
}
/** Deletes the found pairs (dismissed ones too when asked) and queues a new full scan. */
export function useResetScan() {
  const inv = useInvalidate();
  return useMutation({ mutationFn: async (input: { includeDismissed: boolean }) => (await api.post<{ deleted: number }>('/duplicates/scan/reset', input)).data, onSuccess: inv });
}
export function useMergeCandidate() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: async ({ id, survivor, fields }: { id: string; survivor: 'a' | 'b'; fields?: Record<string, 'a' | 'b'> }) => (await api.post(`/duplicates/${id}/merge`, { survivor, fields })).data,
    onSuccess: inv,
  });
}
/** Undoes a merged contact pair; `partial` when an older merge could only give back the contact itself. */
export function useUnmergeCandidate() {
  const inv = useInvalidate();
  return useMutation({ mutationFn: async (id: string) => (await api.post<UnmergeResult>(`/duplicates/${id}/unmerge`)).data, onSuccess: inv });
}
export function useDismissCandidate() {
  const inv = useInvalidate();
  return useMutation({ mutationFn: async (id: string) => (await api.post(`/duplicates/${id}/dismiss`)).data, onSuccess: inv });
}
/** Starts a background run merging the selected pairs; its progress shows like a mass merge. */
export function useMergeSelected() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => (await api.post<MassMergeRun>('/duplicates/merge-selected', { ids })).data,
    // the dialog opens on this run's progress straight away
    onSuccess: (run) => qc.setQueryData(['mass-merge-latest'], run),
  });
}
export function useDismissCandidates() {
  const inv = useInvalidate();
  return useMutation({ mutationFn: async (input: DismissCandidatesInput) => (await api.post<{ dismissed: number }>('/duplicates/dismiss', input)).data, onSuccess: inv });
}
export function useMergeRecords() {
  const inv = useInvalidate();
  return useMutation({ mutationFn: async (input: MergeRecordsInput) => (await api.post('/duplicates/merge', input)).data, onSuccess: inv });
}

export const useMassMergePreview = (tier: number, enabled: boolean) =>
  useQuery({ queryKey: ['mass-merge-preview', tier], queryFn: async () => (await api.get<MassMergePreview>('/duplicates/mass-merge/preview', { params: { tier } })).data, enabled, staleTime: 0 });
/** The latest mass merge; polls while it runs. */
export function useLatestMassMerge() {
  return useQuery({
    queryKey: ['mass-merge-latest'],
    queryFn: async () => (await api.get<{ run: MassMergeRun | null }>('/duplicates/mass-merge/latest')).data.run,
    refetchInterval: (q) => (q.state.data?.status === 'RUNNING' ? 2_000 : false),
  });
}
export function useStartMassMerge() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: async (input: StartMassMergeInput) => (await api.post<MassMergeRun>('/duplicates/mass-merge', input)).data, onSuccess: () => void qc.invalidateQueries({ queryKey: ['mass-merge-latest'] }) });
}
export function useStopMassMerge() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: async (id: string) => (await api.post<MassMergeRun>(`/duplicates/mass-merge/${id}/stop`)).data, onSuccess: () => void qc.invalidateQueries({ queryKey: ['mass-merge-latest'] }) });
}
/** Refreshes the duplicate queue and the records after merges done elsewhere (a mass merge on the worker). */
export const useRefreshAfterMerge = useInvalidate;

export const useDuplicateReasons = (objectType: 'CONTACT' | 'ORGANIZATION', enabled: boolean, polling = false) =>
  useQuery({
    queryKey: ['duplicate-reasons', objectType],
    queryFn: async () => (await api.get<DuplicateReasonGroup[]>('/duplicates/reasons', { params: { objectType } })).data,
    enabled,
    refetchInterval: polling ? 2_000 : false,
  });
export function useDismissByReasons() {
  const inv = useInvalidate();
  return useMutation({ mutationFn: async (input: DismissByReasonsInput) => (await api.post<{ dismissed: number }>('/duplicates/dismiss-by-reasons', input)).data, onSuccess: inv });
}
